import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'
import { Pagination, EffectFade, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'
import { Link } from 'react-router-dom'
import { api, getListItems } from '../../services/api'
import { getImageUrl } from '../../utils/imageUrl'
import { getImageTonePalette } from '../../utils/imageTone'
import { useSiteContent, sectionField } from '../../hooks/useSiteContent'

const DEFAULT_HERO_IMAGE = '/assets/img/hero/hero_bg_1_1.jpg'

function BannerOne() {
    const swiperRef = useRef(null)
    const [slides, setSlides] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [palettes, setPalettes] = useState({})
    const { content: homeContent } = useSiteContent('home')

    useEffect(() => {
        let active = true
        api.getBanners('home_hero')
            .then((res) => {
                if (!active) return
                const items = getListItems(res)
                setSlides(
                    items.map((item) => ({
                        id: item.id,
                        subtitle: item.subtitle || '',
                        title: item.title || '',
                        image: getImageUrl(item.image, 'hero') || DEFAULT_HERO_IMAGE,
                        button_text: item.button_text || 'Explore Tours',
                        button_url: item.button_url || '/tour',
                    }))
                )
            })
            .catch(() => {
                if (active) setSlides([])
            })
            .finally(() => {
                if (active) setLoaded(true)
            })
        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            const entries = await Promise.all(
                slides.map(async (slide) => [slide.id, await getImageTonePalette(slide.image)])
            )
            if (!cancelled) {
                setPalettes(Object.fromEntries(entries))
            }
        })()
        return () => {
            cancelled = true
        }
    }, [slides])

    useEffect(() => {
        const animationProperties = () => {
            document.querySelectorAll('[data-ani]').forEach((element) => {
                const animationName = element.getAttribute('data-ani')
                element.classList.add(animationName)
            })
            document.querySelectorAll('[data-ani-delay]').forEach((element) => {
                const delayTime = element.getAttribute('data-ani-delay')
                element.style.animationDelay = delayTime
            })
        }
        animationProperties()
    }, [slides])

    const handleSliderNavigation = (direction) => {
        if (swiperRef.current && swiperRef.current.swiper) {
            const swiper = swiperRef.current.swiper
            if (direction === 'prev') swiper.slidePrev()
            else swiper.slideNext()
        }
    }

    if (!loaded || !slides.length) {
        return null
    }

    return (
        <div className="th-hero-wrapper hero-1" id="hero">
            <Swiper
                key={slides.map((s) => s.id).join('-')}
                ref={swiperRef}
                modules={[Navigation, Pagination, EffectFade]}
                effect="fade"
                loop={slides.length > 1}
                speed={1000}
                pagination={{
                    el: '.swiper-pagination',
                    clickable: true,
                }}
                navigation={{
                    nextEl: '.slider-next',
                    prevEl: '.slider-prev',
                }}
                className="th-slider hero-slider-1"
                id="heroSlide1"
            >
                <div className="swiper-wrapper">
                    {slides.map((slide) => {
                        const palette = palettes[slide.id]
                        return (
                        <SwiperSlide key={slide.id}>
                            <div
                                className={`hero-inner twa-banner-tone twa-banner-tone--${palette?.tone || 'dark'}`}
                                style={palette ? {
                                    '--twa-banner-text': palette.textColor,
                                    '--twa-banner-muted': palette.mutedColor,
                                    '--twa-banner-accent': palette.accentColor,
                                    '--twa-banner-shadow': palette.shadow,
                                    '--twa-btn-bg': palette.btnPrimaryBg,
                                    '--twa-btn-color': palette.btnPrimaryText,
                                    '--twa-btn-border': palette.btnPrimaryBorder,
                                    '--twa-btn-hover-bg': palette.btnPrimaryHoverBg,
                                    '--twa-btn-hover-color': palette.btnPrimaryHoverText,
                                    '--twa-btn2-bg': palette.btnSecondaryBg,
                                    '--twa-btn2-color': palette.btnSecondaryText,
                                    '--twa-btn2-border': palette.btnSecondaryBorder,
                                    '--twa-btn2-hover-bg': palette.btnSecondaryHoverBg,
                                    '--twa-btn2-hover-color': palette.btnSecondaryHoverText,
                                } : undefined}
                            >
                                <div
                                    className="th-hero-bg"
                                    style={{
                                        backgroundImage: `url(${slide.image})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'cover',
                                    }}
                                />
                                {palette && (
                                    <div
                                        className="twa-banner-overlay"
                                        style={{ background: palette.overlay }}
                                        aria-hidden="true"
                                    />
                                )}
                                <div className="container">
                                    <div className="hero-style1">
                                        {slide.subtitle && (
                                            <span
                                                className="sub-title style1"
                                                data-ani="slideinup"
                                                data-ani-delay="0.2s"
                                            >
                                                {slide.subtitle}
                                            </span>
                                        )}
                                        <h1
                                            className="hero-title"
                                            data-ani="slideinup"
                                            data-ani-delay="0.4s"
                                        >
                                            {slide.title}
                                        </h1>
                                        <div
                                            className="btn-group"
                                            data-ani="slideinup"
                                            data-ani-delay="0.6s"
                                        >
                                            <Link to={slide.button_url || '/tour'} className="th-btn th-icon">
                                                {slide.button_text || 'Explore Tours'}
                                            </Link>
                                            <Link
                                                to={sectionField(homeContent, 'hero', 'secondary_cta_url', '/service')}
                                                className="th-btn style2 th-icon"
                                            >
                                                {sectionField(homeContent, 'hero', 'secondary_cta_text', 'Our Services')}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                        )
                    })}
                </div>
                <div className="th-swiper-custom">
                    <button
                        className="slider-arrow slider-prev"
                        onClick={() => handleSliderNavigation('prev')}
                        type="button"
                    >
                        <img src="/assets/img/icon/right-arrow.svg" alt="Prev" />
                    </button>
                    <div className="swiper-pagination" />
                    <button
                        className="slider-arrow slider-next"
                        onClick={() => handleSliderNavigation('next')}
                        type="button"
                    >
                        <img src="/assets/img/icon/left-arrow.svg" alt="Next" />
                    </button>
                </div>
            </Swiper>
        </div>
    )
}

export default BannerOne
